import React, { useEffect, useState } from 'react';
import { Book, BookHistoryEvent } from '../objects/Book';
import { getBookHistory } from '../repository/readingRepository';

interface BookHistoryProps {
  book: Book;
}

const formatDate = (isoDate: string): string => {
  const date = new Date(isoDate);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/New_York',
  });
};

const formatEventDescription = (event: BookHistoryEvent): string => {
  switch (event.event_type) {
    case 'book_added':
      return `Added to library with status "${event.details.initial_status}"`;
    case 'status_changed':
      return `Status changed from "${event.details.old_status}" to "${event.details.new_status}"`;
    case 'note_created':
      return 'Notes created';
    case 'note_updated':
      return 'Notes updated';
    default:
      return 'Unknown event';
  }
};

const BookHistory: React.FC<BookHistoryProps> = ({ book }) => {
  const [history, setHistory] = useState<BookHistoryEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    const fetchHistory = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getBookHistory(book.uuid);
        if (isMounted) {
          setHistory(data);
        }
      } catch (err) {
        if (isMounted) {
          setError('Failed to load history');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    fetchHistory();
    return () => {
      isMounted = false;
    };
  }, [book.uuid]);

  if (loading) {
    return <div className="helper-text">Loading history...</div>;
  }

  if (error) {
    return <div className="alert error">{error}</div>;
  }

  if (history.length === 0) {
    return <div className="helper-text">No history recorded yet.</div>;
  }

  return (
    <div className="history-list">
      {history.map((event) => (
        <div key={event.uuid} className="history-item">
          <div className="history-date">{formatDate(event.created_at)}</div>
          <div className="history-description">{formatEventDescription(event)}</div>
        </div>
      ))}
    </div>
  );
};

export default BookHistory;
